import React, { PureComponent } from "react";
import Hawsehole from "hawsehole";
import { connect } from "react-redux";
import { createSelector, createStructuredSelector } from "reselect";
import Transition from "join-transition";
import Sizeable from "react-sizeable";
import { format, scaleOrdinal, extent, timeParse, timeFormat } from "d3";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactTooltip from 'react-tooltip';

import Nav from "../Nav";
import Graphic from "../Graphic";
import Flows from "../Flows";
import Timeline from "../Timeline";
import CompositionTable from "../components/CompositionTable";
import KeyMetrics from "../components/KeyMetrics";
import Radio from "../components/Radio";
import BarGraph from "../components/BarGraph";
import FilterCheck from "../components/FilterCheck";
import CustomStressTest from '../CustomStressTest';
import MonteCarlo from '../MonteCarlo';
import EditableName from '../components/EditableName';
import loadingIndicator from '../assets/local-loading.gif';

import stickyfiller from "../utils/stickyfiller";
import { dataSelector } from "./selectors";
import { retrievePDF } from "../actions/save-to-pdf";
import { retrieveTicket } from "../actions/get-trade-ticket";
import loadPortfolioHistory from '../actions/query-api/list-portfolios.js';

import { IMPT_INFO_default, TEST_INFO_default } from '../constants.js'

import "./style.css";

const NAV_HEIGHT = 48;
const EXAMPLE_PORTFOLIO = 'Example';

const Heading = ({ children }) => <h3 ref={stickyfiller}>{children}</h3>;

const Pane = ({ children, additionalClass = "" }) => (
  <div className={"pane " + additionalClass}>
    <div className="pane-inner">{children}</div>
  </div>
);

const Compliance = ({ text, footnote, extraClass, header, dang }) =>
  dang
    ? <div className="compliance" dangerouslySetInnerHTML={{ __html: dang }}></div>
    : <div className={"compliance " + (footnote ? "pdf-footnote" : "")}>
      {header
        ? <p><span className={extraClass}>{header}</span><span>{text}</span></p>
        : <p className={extraClass}>{text}</p>}
    </div>;

class Report extends PureComponent {

  constructor(props) {
    super(props);
    this.reportNameUpdated = this.reportNameUpdated.bind(this);
    this.handleCloseMyPortfoliosModal = this.handleCloseMyPortfoliosModal.bind(this);
    this.handleSaveMyPortfoliosModal = this.handleSaveMyPortfoliosModal.bind(this);
    this.handleOpenMyPortfoliosModal = this.handleOpenMyPortfoliosModal.bind(this);
    this.handleUpdatePortfolioName = this.handleUpdatePortfolioName.bind(this);    

    this.state = {
      saveMyPortfoliosModelSideOpen : null,
      newMyPortfolioName: null,
    };
  }

  componentDidMount() {
    if (this.props.salesforceComplete) {
      this.props.retrieveTicket();
      setTimeout(() => {
        this.props.retrievePDF();
      }, 1500);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.salesforceComplete) {
      if (this.props.ticketStatus === "unsent") {
        this.props.retrieveTicket();
      }
      setTimeout(() => {
        if (this.props.pdfStatus === "unsent") {
          this.props.retrievePDF();
        }
      }, 1500)
    }

  }

  // Redo the pdf if the report name is updated
  reportNameUpdated() {
    if (this.props.salesforceComplete) {
      setTimeout(() => {
        if (this.props.pdfStatus !== "pending") {
          this.props.retrievePDF();
        }
      }, 1500)
    }
  }
  
  // Handle update portfolio name
  handleUpdatePortfolioName(name) {
    this.setState({
      newMyPortfolioName: name,
    })
  }

  // Handle close of modal
  handleCloseMyPortfoliosModal() {
    this.setState({
      saveMyPortfoliosModelSideOpen: null,
      newMyPortfolioName: null,
    })
  }

  // Handle open of modal
  handleOpenMyPortfoliosModal(side) {

    const {
      originalPortfolioName,
      proposedPortfolioName,
      TEXT
    } = this.props;
    
    this.setState({
      saveMyPortfoliosModelSideOpen: side,
      newMyPortfolioName: side === 'original' ? originalPortfolioName : 
        (proposedPortfolioName ? proposedPortfolioName : originalPortfolioName + ' - ' + TEXT.proposedPortfolioLabel.split(' ')[0]),
    })
  }

  // Handle save of portfolio to My Portfolios
  handleSaveMyPortfoliosModal(side){

    const {
      data,
      saveToPersonalStrategies,
    } = this.props;

    const {
      saveMyPortfoliosModelSideOpen,
      newMyPortfolioName,
    } = this.state;

    saveToPersonalStrategies(data.raw[saveMyPortfoliosModelSideOpen === 'original' ? 0 : 1].id, saveMyPortfoliosModelSideOpen, newMyPortfolioName);

    this.setState({
      saveMyPortfoliosModelSideOpen: null,
    })
  }

  render() {
    const {
      data,
      width,
      compareMode,
      currency,
      wtRegion,
      incomeSplit,
      performanceSplit,
      compositionSplit,
      monteCarloSplit,
      colorScale,
      pdfStatus,
      keyMetrics,
      compositionFields,
      famaFrench5FactorFields,
      performanceTableFields,
      fundDetailsFields,
      stressTests,
      graphicLayout,
      graphicAggregateBy,
      investmentGoal,
      bestModel,
      originalPortfolioName,
      proposedPortfolioName,
      clientPdf,
      TEXT,
      features,
      clearAnalysis,
      originalUsingModel,
      proposedUsingModel,
      pendingSaveOriginal,
      pendingSaveProposed,
      reportName,
    } = this.props;

    const {
      setPerformanceSplit,
      setIncomeSplit,
      setCompositionSplit,
      setMonteCarloSplit,
      setGraphicLayout,
      setGraphicAggregateBy,
      dialogLeavingWTSite,
      setReportName,
    } = this.props;
    const proposedNotInOriginalUSA = data.uniques.proposed
      .filter(t => data.tickersUSA.proposed.map(o => o.ticker).indexOf(t.ticker) !== -1);

    const performanceTableData = [
      ...data.holdings.original,
      ...data.uniques.proposed.filter(o => o.tickerWeighting)
    ].filter(d => d.type !== "CS")

    const performanceDates = extent(data.levels.map(o => timeParse("%Y-%m-%dT%H:%M:%S")(o.date)
      || timeParse("%Y-%m-%d")(o.date)))
      .reduce((t, v, i) => t + (i > 0 ? " to " : "") + timeFormat("%m/%d/%Y")(v), "");
    const performanceDrawdownDates = extent(data.levels.map(o => timeParse("%Y-%m-%dT%H:%M:%S")(o.dateDrawdowns)
      || timeParse("%Y-%m-%d")(o.dateDrawdowns)))
      .reduce((t, v, i) => t + (i > 0 ? " to " : "") + timeFormat("%m/%d/%Y")(v), "");
    const getCleanDate = date => timeFormat("%m/%d/%Y")(timeParse("%Y-%m-%dT%H:%M:%S")(date));

    const commonPeriodStart = data.raw[0].commonPeriodStart;
    const commonPeriodEnd = data.raw[0].commonPeriodEnd;
    const pdfLeftMargins = {
      sector: 170,
      country: 120,
      marketCap: 100,
      assetClass: 100,
      creditQuality: 100,
      maturity: 170,
      // currency: 100,
    }

    const reportWidth = width;

    const IMPT_INFO = window.portfolioMapper.importantInformationURL || IMPT_INFO_default
    const TEST_INFO = window.portfolioMapper.stressTestMethodologyURL || TEST_INFO_default

    const exposureOptionsLabels = {
      country: TEXT.geographyExposuresLabel,
      marketCap: TEXT.marketCapExposuresLabel,
      assetClass: TEXT.assetClassExposuresLabel,
      sector: TEXT.sectorExposuresLabel,
      creditQuality: TEXT.creditQualityExposuresLabel,
      maturity: TEXT.maturityExposuresLabel,
    }

    const exposuresDisclosuresLookup = {
      country: "geographicExposuresDisclosure",
      marketCap: "marketCapExposuresDisclosure",
      assetClass: "assetClassExposuresDisclosure",
      sector: "sectorExposuresDisclosure",
      creditQuality: "creditQualityExposuresDisclosure",
      maturity: "maturityExposuresDisclosure",
    }

    const exposureOptions = this.props.exposureOptions.map((d, i) => ({ value: d.value, label: exposureOptionsLabels[d.value] }));

    // Set up the portfolio names based one whether a name was provided or if the portfolio is a Model
    const originalPortfolioLabel = (originalPortfolioName && originalPortfolioName !== EXAMPLE_PORTFOLIO) ? originalPortfolioName : TEXT.originalPortfolioLabel;
    const proposedPortfolioLabel = (proposedPortfolioName && proposedPortfolioName !== EXAMPLE_PORTFOLIO) ? proposedPortfolioName : TEXT.proposedPortfolioLabel;
    const originalPortfolioShortLabel = (originalPortfolioName && originalPortfolioName !== EXAMPLE_PORTFOLIO) ? originalPortfolioName : TEXT.originalPortfolioLabel.split(" ")[0];

    // Set up the report name
    let reportNameLabel = '';
    if (reportName) {
      reportNameLabel = reportName.toUpperCase();
    } else {
      reportNameLabel = TEXT.unnamedPortfolioLabel.toUpperCase();
    }

    return (
      <div className="report">
        <Hawsehole
          navComponent={Nav}
          hash={true}
          listNames={true}
          anchorTop={node =>
            window.pageYOffset +
            node.parentNode.getBoundingClientRect().top -
            NAV_HEIGHT} >

          {clientPdf.description && <section className="pdf-only overview">
            <h4>{TEXT.clientPDFOverview}</h4>
            <div className="overview-boxed">
              <h3>Overview</h3>
              <div className="contents">{clientPdf.description.split("\n").map((o, i) => <p key={"p-" + i}>{o}</p>)}</div>
            </div>
          </section>}

          <section className="dashboard">
            <a name={TEXT.dashboardBreadcrumbsLabel}></a>
            <Heading>

              <span className='dashboard-header'>
                <span className='dashboard-header-details'>
                  {TEXT.dashboardLabel}
                  <small><em> {"(as of " + timeFormat("%m/%d/%Y")(new Date()) + ")"}</em></small>
                  {wtRegion !== 'US' && <small>Currency: {currency}</small>}
                  <small className="modal-link">
                    <a href={IMPT_INFO} target="_blank">{TEXT.ImportantInformationLabel}</a>
                  </small>
                </span>
                <span className='dashboard-header-name hide-client-pdf'>
                  <EditableName
                    TEXT={TEXT}
                    value={reportNameLabel}
                    onChange={value => setReportName(value)}
                    version="name"
                    onBlur={this.reportNameUpdated}
                    side='report-'
                  />
                </span>
              </span>
            </Heading>
            <div className="contents">
              <Pane>
                <h4>
                  <div className='fund-composition'>
                    <div>{TEXT.fundCompositionLabel}</div>
                    <div className='hide-client-pdf'>
                      <button onClick={clearAnalysis}>
                        {TEXT.editPortfoliosLabel}
                      </button>
                    </div>
                  </div>
                </h4>
                <div className="fund-composition-headings">
                  <div className="original version-heading">
                    {originalPortfolioLabel}

                    {/* If the portfolio is being saved or complete show an icon, otherwise pending gif */}
                    {
                      features.personalStrategiesInput ?

                        <span
                          data-tip
                          data-for='add-to-my-portfolios-tooltip-original'
                          className='save-icon hide-client-pdf'>

                          {
                            (pendingSaveOriginal.complete ?
                              <FontAwesomeIcon
                                className='saved-to-my-portfolios'
                                icon="check-square"
                                size="lg"
                              />
                              : (pendingSaveOriginal.portfolioId ?
                                <img
                                  className='saving'
                                  key="0"
                                  src={loadingIndicator}
                                  alt="saving" />
                                : <FontAwesomeIcon
                                  className='add-to-my-portfolios'
                                  icon="folder-plus"
                                  size="lg"
                                  onClick={this.handleOpenMyPortfoliosModal.bind(this, 'original')}
                                />
                              ))
                          }

                          <ReactTooltip
                            key='add-to-my-portfolios-tooltip-original'
                            id='add-to-my-portfolios-tooltip-original'
                            type='light'
                            className='tooltip hide-client-pdf'
                            effect="float">
                            {pendingSaveOriginal.complete ? TEXT.savedToMyPortfoliosLabel : TEXT.clickToSaveToMyPortfoliosLabel}
                          </ReactTooltip>
                        </span>
                        : null
                    }
                    
                  </div>
                  <div className="proposed version-heading">
                    {proposedPortfolioLabel}
                    
                    {/* If the portfolio is being saved or complete show an icon, otherwise pending gif */}
                    {
                      features.personalStrategiesInput ?

                        <span
                          data-tip
                          data-for='add-to-my-portfolios-tooltip-proposed'
                          className='save-icon hide-client-pdf'>

                          {pendingSaveProposed.complete ?
                            <FontAwesomeIcon
                              className='saved-to-my-portfolios'
                              icon="check-square"
                              size="lg"
                            />
                            : (pendingSaveProposed.portfolioId ?
                              <img
                                className='saving'
                                key="0"
                                src={loadingIndicator}
                                alt="saving" />
                              :
                              <FontAwesomeIcon
                                className='add-to-my-portfolios'
                                icon="folder-plus"
                                size="lg"
                                onClick={this.handleOpenMyPortfoliosModal.bind(this, 'proposed')}
                              />)
                          }
                          <ReactTooltip
                            key='add-to-my-portfolios-tooltip-proposed'
                            id='add-to-my-portfolios-tooltip-proposed'
                            type='light'
                            className='tooltip hide-client-pdf'
                            effect="float">
                            {pendingSaveProposed.complete ? TEXT.savedToMyPortfoliosLabel : TEXT.clickToSaveToMyPortfoliosLabel}
                          </ReactTooltip>
                        </span>
                        : null
                    }
                  </div>


                  {this.state.saveMyPortfoliosModelSideOpen
                    ? 
                    <div
                      className="wt-dialog modal fade restricted-overlay in"
                      id="externalLinkModal"
                      tabIndex="-1"
                      role="dialog"
                      style={{ display: "block", paddingRight: "15px" }}>

                      <div className="modal-dialog" role="document">
                        <div className="modal-content">
                          <div className="modal-body">

                            <div className='modal-edit-name'>                            
                              <EditableName
                                TEXT={TEXT}
                                value={this.state.newMyPortfolioName}
                                onChange={name => this.handleUpdatePortfolioName(name)}
                                version="name"
                                // onBlur={this.reportNameUpdated}
                                side='report-'
                              />    
                            </div> 
                            <br/> 
                            <div className="form-group">
                              {TEXT.modalSaveToMyPortfoliosLablel}
                            </div>   
                            <br/>
                            <div className='modal-buttons'>                    
                              <button 
                                type="button" 
                                data-dismiss="modal" 
                                onClick={this.handleSaveMyPortfoliosModal}>
                                {TEXT.saveLabel}
                              </button>
                              <button 
                                type="button" 
                                data-dismiss="modal" 
                                onClick={this.handleCloseMyPortfoliosModal}>
                                {TEXT.cancelButton}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    : null }

                  {/* <Modal 
                    open={this.state.saveMyPortfoliosModelSideOpen}
                    onClose={this.handleCloseMyPortfoliosModal}
                    showCloseIcon={false}
                    classNames={{ overlay: 'modal-overlay', modal: 'confirm-modal', closeIcon: 'custom-closeIcon' }} 
                    center
                    >
                      <div>
                          <br />
                          
                          Confirm Save

                          <div className='align-right'>
                              <button onClick={this.handleSaveMyPortfoliosModal}>Save</button>
                              <button onClick={this.handleCloseMyPortfoliosModal}>Cancel</button>
                          </div>
                      </div>
                  </Modal> */}

                </div>

                <Sizeable>
                  <Flows
                    data={data.alignedHoldings}
                    keyBy="ticker"
                    aggregateBy="ticker"
                    colorScale={colorScale}
                    dialogLeavingWTSite={dialogLeavingWTSite}
                  />
                </Sizeable>
                {!compareMode && <FilterCheck />}
              </Pane>

              <Pane>
                <h4>{TEXT.keyMetricsLabel}</h4>
                <KeyMetrics
                  metrics={[
                    ...keyMetrics.filter(k => k.key === investmentGoal),
                    ...keyMetrics.filter(k => k.key !== investmentGoal)
                  ]}
                  original={data.raw[0].statistics}
                  proposed={data.raw[1].statistics}
                  commonPeriodStart={commonPeriodStart}
                  commonPeriodEnd={commonPeriodEnd}
                  asOfDate={data.date.original}
                  getCleanDate={getCleanDate}
                  originalPortfolioLabel={originalPortfolioLabel}
                  proposedPortfolioLabel={proposedPortfolioLabel}
                />
              </Pane>
              <Compliance footnote={true}
                text={"*Common period is from "
                  + getCleanDate(commonPeriodStart)
                  + " to "
                  + getCleanDate(commonPeriodEnd)
                  + "."} />
              <Compliance extraClass="big" dang={TEXT.dashboardDisclosure} />
            </div>

          </section>

          <section className="performance">
            <a name={TEXT.performanceBreadcrumbsLabel}></a>
            <Heading>
              <div style={{ float: "left" }}>{TEXT.portfolioPerformanceLabel}</div>
              <div className="options">
                <Radio
                  values={[TEXT.compareViewButton, TEXT.isolateViewButton]}
                  selected={
                    performanceSplit ? TEXT.isolateViewButton : TEXT.compareViewButton
                  }
                  onChange={value =>
                    setPerformanceSplit(value === TEXT.isolateViewButton)}
                />
              </div>
            </Heading>

            <div className="sub-contents">
              <Transition values={+performanceSplit} duration={600}>
                {performanceSplit => (
                  <div>
                    <Pane>
                      <h4 className="narrow">
                        {TEXT.hypotheticalGrowthOf1MLabel}
                        <small>
                          {" (" + performanceDates + ")"}
                        </small>
                      </h4>
                      <Sizeable>
                        <Timeline
                          data={data.levels}
                          benchmarks={data.benchmarksLevels}
                          before={data.versions.original}
                          after={data.versions.proposed}
                          x="date"
                          y="uv1MNAV"
                          baseline={10e5}
                          split={performanceSplit}
                          colorScale={colorScale}
                          xFormat="%b %Y"
                          yFormat="2s"
                          detailFormat="$,.0f"
                          isVertical={true}
                          reportWidth={reportWidth}
                          originalPortfolioLabel={originalPortfolioLabel}
                          proposedPortfolioLabel={proposedPortfolioLabel}
                          originalPortfolioShortLabel={originalPortfolioShortLabel}
                        />
                      </Sizeable>
                      <Compliance dang={TEXT.hypotheticalGrowthDisclosure} />
                    </Pane>

                    <Pane>
                      <h4 className="narrow">
                        {TEXT.monthlyRollingDrawdownsLabel}
                        <small>
                          {" (" + performanceDrawdownDates + ")"}
                        </small>
                      </h4>
                      <Sizeable>
                        <Timeline
                          data={data.drawdownLevels}
                          benchmarks={data.benchmarksDrawdownLevels}
                          before={data.versions.original}
                          after={data.versions.proposed}
                          x="dateDrawdowns"
                          y="monthlyRollingDrawdowns"
                          split={performanceSplit}
                          colorScale={colorScale}
                          xFormat="%b %Y"
                          yFormat=".1%"
                          detailFormat=".1%"
                          isVertical={true}
                          reportWidth={reportWidth}
                          originalPortfolioLabel={originalPortfolioLabel}
                          proposedPortfolioLabel={proposedPortfolioLabel}
                          originalPortfolioShortLabel={originalPortfolioShortLabel}
                        />
                      </Sizeable>
                      <Compliance dang={TEXT.drawdownsDisclosure} />
                    </Pane>
                  </div>
                )}
              </Transition>
            </div>

            <div className="contents">
              {data.performance.map(p => (
                <CompositionTable
                  TEXT={TEXT}
                  key={p[0].performanceTitle}
                  rows={p}
                  fields={p[0].performanceTitle !== TEXT.returnsPerformanceTableLabel
                    ? performanceTableFields.filter(o => o.key !== "inception" && (o.group === "Average" || o.group === "CP"))
                    : performanceTableFields.filter(o => o.key !== "inception")}
                  tableType="overall"
                  alternateFormat={true}
                  performanceTitle={p[0].performanceTitle}
                  footnote={p[0].performanceTitle !== TEXT.returnsPerformanceTableLabel
                    ? null
                    : TEXT.performanceDescLabel
                      .replace("[[FROMDATE]]", getCleanDate(commonPeriodStart))
                      .replace("[[TODATE]]", getCleanDate(commonPeriodEnd))}
                  originalPortfolioLabel={originalPortfolioLabel}
                  proposedPortfolioLabel={proposedPortfolioLabel}
                />
              ))}
              <Compliance dang={TEXT.stdPerformanceDisclosure} />
              {originalUsingModel || proposedUsingModel ? <Compliance dang={TEXT.modelPerformanceDisclosure} /> : null}
              <Compliance footnote={true} //pdf only
                text={"*" + TEXT.performanceDescLabel
                  .replace("[[FROMDATE]]", getCleanDate(commonPeriodStart))
                  .replace("[[TODATE]]", getCleanDate(commonPeriodEnd))} />
            </div>

            <div className="calendar-year-performance">
              <h4 className="subtitle">{TEXT.calendarYearPerformanceLabel}</h4>
              <table className="composition-table">
                <thead>
                  <tr>
                    <th className="header border-right" rowSpan="2">{TEXT.calendarYearLabel}</th>
                    <th className="center header" colSpan={2 + data.calendarYearPerformance[0].benchmarksNames.length}>{TEXT.totalReturnsLabel.toUpperCase()}</th>
                  </tr>
                  <tr>
                    <th className="original">{originalPortfolioLabel}</th>
                    <th className="proposed">{proposedPortfolioLabel}</th>
                    {data.calendarYearPerformance[0].benchmarksNames.map(n => <th key={n}>{n}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.calendarYearPerformance.map(d => <tr key={d.key}>
                    <td className="header">{d.key}</td>
                    <td>{d.original ? format(".2%")(d.original) : "--"}</td>
                    <td>{d.proposed ? format(".2%")(d.proposed) : "--"}</td>
                    {d.benchmarks.map((v, i) => <td key={'th-' + i}>{format(".2%")(v)}</td>)}
                  </tr>)}
                </tbody>
              </table>
            </div>

            {!features.fundamentals || (Array.isArray(data.aggregateFundamentals) && data.aggregateFundamentals.length === 0) ? null :
              <div className="aggregate-fundamentals">
                <h4 className="subtitle">{TEXT.aggregateFundamentalsLabel}</h4>
                <table className="composition-table">
                  <thead>
                    <tr>
                      <th className="header">{TEXT.fieldLabel}</th>
                      <th className="original">{originalPortfolioLabel}</th>
                      <th className="proposed">{proposedPortfolioLabel}</th>
                      {/* Remove the Benchmark column until we have non-benchmark fundamentals at portfolio level */}
                      {/* {data.aggregateFundamentals[0]
                      && data.aggregateFundamentals[0].benchmarksNames.map((n,i) => <th key={'th-'+i}>{n}</th>)} */}
                    </tr>
                  </thead>
                  <tbody>
                    {data.aggregateFundamentals.map(d => <tr key={d.key}>
                      <td className="header">{d.key}</td>
                      <td>{this.formatFundamentals(d.key, d.original)}</td>
                      <td>{this.formatFundamentals(d.key, d.proposed)}</td>
                      {/* Remove the Benchmark column until we have non-benchmark fundamentals at portfolio level */}
                      {/* {d.benchmarks && d.benchmarks.map((v,i) => 
                      <td key={'td-'+i}>{v ? this.formatFundamentals(d.key, v): '-'}</td>)} */}
                    </tr>)}
                  </tbody>
                </table>
              </div>}

          </section>

          <section className="income">
            <a name={TEXT.incomeBreadcrumbsLabel}></a>
            <Heading>
              <div style={{ float: "left" }}>{TEXT.portfolioIncomeLabel}</div>
              <div className="options">
                <Radio
                  values={[TEXT.compareViewButton, TEXT.isolateViewButton]}
                  selected={incomeSplit ? TEXT.isolateViewButton : TEXT.compareViewButton}
                  onChange={value => setIncomeSplit(value === TEXT.isolateViewButton)}
                />
              </div>
            </Heading>

            <div className="contents">
              <Transition values={+incomeSplit} duration={600}>
                {incomeSplit => (
                  <div>
                    <Pane>
                      <h4>{TEXT.hypotheticalIncomeOn1MLabel}
                        <span className="unbold">{" " + TEXT.hypotheticalIncomeByCalendarYearLabel}</span>
                      </h4>
                      <Sizeable>
                        <Timeline
                          data={data.aggregateDistributions}
                          before={data.versions.original}
                          after={data.versions.proposed}
                          x="calendarYear"
                          y="ordinaryIncome"
                          xFormat="%Y"
                          split={incomeSplit}
                          showBars={true}
                          showArea={false}
                          reportWidth={reportWidth}
                          isVertical={true}
                          colorScale={colorScale}
                          originalPortfolioLabel={originalPortfolioLabel}
                          proposedPortfolioLabel={proposedPortfolioLabel}
                          originalPortfolioShortLabel={originalPortfolioShortLabel}
                        />
                      </Sizeable>
                    </Pane>
                    <Pane>
                      <h4>{TEXT.hypotheticalIncomeByAggregateLabel}
                        <span className="unbold">{" " + TEXT.hypotheticalIncomeTotalLabel}</span>
                      </h4>
                      <Sizeable>
                        <Timeline
                          data={data.aggregateAggregateDistributions}
                          before={data.versions.original}
                          after={data.versions.proposed}
                          x="calendarYear"
                          xLabel="calendarYearLabel"
                          y="ordinaryIncome"
                          yFormat=",.0f"
                          split={incomeSplit}
                          showBars={true}
                          showArea={false}
                          totalBar={true}
                          reportWidth={reportWidth}
                          isVertical={true}
                          colorScale={colorScale}
                          originalPortfolioLabel={originalPortfolioLabel}
                          proposedPortfolioLabel={proposedPortfolioLabel}
                          originalPortfolioShortLabel={originalPortfolioShortLabel}
                        />
                      </Sizeable>
                    </Pane>
                  </div>
                )}
              </Transition>
              <Compliance dang={TEXT.incomeDisclosure} />
            </div>
          </section>

          {/* Only show exposures section if any of the exposures are enabled */}
          {Array.isArray(exposureOptions) === false || exposureOptions.length === 0 ? null :
            <section className="exposures">
              <a name={TEXT.exposuresBreadcrumbsLabel}></a>
              <Heading>
                <div style={{ float: "left" }}>{TEXT.portfolioExposuresLabel}</div>
                <div className="options">
                  <Radio
                    type="list"
                    values={exposureOptions}
                    selected={graphicAggregateBy}
                    onChange={setGraphicAggregateBy}
                    disable={pdfStatus === "unsent"}
                  />
                  <Radio
                    values={[{ value: "Bar chart", label: TEXT.barChartButton }, { value: "Treemap", label: TEXT.treemapButton }]}
                    selected={graphicLayout}
                    onChange={setGraphicLayout}
                    disable={pdfStatus === "unsent"}
                  />
                </div>
              </Heading>

              <div>
                <Pane>
                  <div className="original version-heading">{originalPortfolioLabel}</div>
                </Pane>
                <Pane>
                  <div className="proposed version-heading swing">{proposedPortfolioLabel}</div>
                </Pane>

                {(pdfStatus === "unsent"
                  ? exposureOptions
                  : exposureOptions.filter(option => option.value === graphicAggregateBy))
                  .map((option, i) =>
                    <div key={option.value} className="contents">
                      {pdfStatus === "unsent" && <h4 className="subtitle">{option.label}</h4>}
                      <Sizeable>
                      <Graphic
                        data={data.exposures[option.value]}
                        benchmarks={data.benchmarksExposures.map(b => b[option.value].map(e => ({ ...e, name: b.name })))}
                        keyBy="value"
                        aggregateBy={graphicLayout === "Treemap" && graphicAggregateBy === "country"
                          ? "group"
                          : "value"}
                        margin={{ left: pdfLeftMargins[option.value] }}
                        //width={width}
                        colorScale={colorScale}
                        layout={graphicLayout} />
                      </Sizeable>
                      <Compliance
                        dang={TEXT[exposuresDisclosuresLookup[option.value]]
                          .replace("[[ASOFDATE]]", getCleanDate(data.raw[0][option.value + "Exposures"][0].date))
                          .replace("[[LINK]]", "'" + IMPT_INFO + "#important-risk-information'")} />
                    </div>)}
              </div>
            </section>
          }

          {!features.stressTests ? null :
            <section className="stress_tests">
              <div className="contents">
                <a name={TEXT.stressTestsBreadcrumbsLabel}></a>
                <Heading>{TEXT.stressTestsLabel}</Heading>
                {data.stressTests.map((e, i) => (
                  <Pane key={e.test} additionalClass={"stress-test " + e.test.toLowerCase()}>
                    <h4>{stressTests[e.test].title}</h4>
                    <h4 className="subtitle">
                      <span className="original">{originalPortfolioShortLabel}</span>
                      <span className="small"> vs. </span>
                      <span className="proposed">{proposedPortfolioLabel}</span>
                    </h4>
                    <div>
                      <div className="halfwidth">
                        {e.values[0].value && e.values[1].value
                          ? <Sizeable>
                            <BarGraph
                              data={e.values}
                              x="portfolio"
                              y="value"
                              yFormat=".1%"
                              colorScale={colorScale}
                            />
                          </Sizeable>
                          : <div className="unavailable">
                            <p>Data unavailable</p></div>}
                      </div>
                      <div className="paragraph">
                        <p>{stressTests[e.test].description}</p>
                      </div>
                    </div>
                  </Pane>
                ))}

                {features.customStressTests && data.customStressTestsData.portfolio[0] &&
                  <CustomStressTest
                    data={data.customStressTestsData}
                    portfolios={data.fm16FactorModel}
                    width={width}
                    colorScale={colorScale}
                    TEST_INFO={TEST_INFO}
                    originalPortfolioLabel={originalPortfolioShortLabel}
                    proposedPortfolioLabel={proposedPortfolioLabel}
                  />}
                <br />
                <Compliance dang={TEXT.stressTestDisclosure
                  .replace("[[ASOFDATE]]", timeFormat("%m/%d/%Y")(new Date(data.raw[0].stressTests.date)))
                  .replace(/\[\[LINK\]\]/g, TEST_INFO)} />
              </div>
            </section>}

          {!features.hypotheticalFutureGrowth || !data.monteCarlo || data.monteCarlo.length === 0 ? null :
            <section className="growth">
              <div className="contents">
                <a name={TEXT.growthBreadcrumbsLabel}></a>
                <Heading>
                  <div style={{ float: "left" }}>{TEXT.projectedGrowthLabel}</div>
                  <div className="options">
                    <Radio
                      values={[{ value: "Compare view", label: TEXT.compareViewButton }, { value: "Isolate view", label: TEXT.isolateViewButton }]}
                      selected={
                        monteCarloSplit ? "Isolate view" : "Compare view"
                      }
                      onChange={value =>
                        setMonteCarloSplit(value === "Isolate view")}
                    />
                  </div>
                </Heading>
                <MonteCarlo
                  data={data.monteCarlo}
                  colorScale={colorScale}
                  reportWidth={reportWidth}
                  portfolios={data.fm16FactorModel}
                  versions={data.versions}
                  TEST_INFO={TEST_INFO}
                  originalPortfolioLabel={originalPortfolioLabel}
                  proposedPortfolioLabel={proposedPortfolioLabel}
                  originalPortfolioShortLabel={originalPortfolioShortLabel}
                />
              </div>
            </section>}

          {!data.tickersUSA.original.length && !proposedNotInOriginalUSA.length
            ? null
            : <section className="factor">
              <div className="contents">
                <a name={TEXT.factorsBreadcrumbsLabel}></a>
                <Heading>{TEXT.factorLoadingsLabel}</Heading>
                <div>
                  {!data.tickersUSA.original.length
                    ? null
                    : <CompositionTable
                      TEXT={TEXT}
                      rows={data.tickersUSA.original}
                      fields={famaFrench5FactorFields}
                      tableType="original"
                      alternateFormat={true}
                      originalPortfolioLabel={originalPortfolioLabel}
                      proposedPortfolioLabel={proposedPortfolioLabel}
                    />}
                  {!proposedNotInOriginalUSA.length
                    ? null
                    : <CompositionTable
                      TEXT={TEXT}
                      rows={proposedNotInOriginalUSA.filter(o => o.tickerWeighting)}
                      fields={famaFrench5FactorFields}
                      tableType="proposed"
                      alternateFormat={true}
                      originalPortfolioLabel={originalPortfolioLabel}
                      proposedPortfolioLabel={proposedPortfolioLabel}
                    />}
                  <CompositionTable
                    TEXT={TEXT}
                    rows={(data.tickersUSA.original.length ? [data.famaFrench5Factors[0]] : [])
                      .concat((data.tickersUSA.proposed.length ? [data.famaFrench5Factors[1]] : []))}
                    fields={famaFrench5FactorFields}
                    tableType="overall"
                    alternateFormat={true}
                    originalPortfolioLabel={originalPortfolioLabel}
                    proposedPortfolioLabel={proposedPortfolioLabel}
                  />
                </div>
                <Compliance
                  dang={TEXT.factorLoadingsDisclosure
                    .replace("[[ASOFDATE]]", timeFormat("%m/%d/%Y")(new Date(data.raw[0].stdPerformance[0].asOfDate)))} />
              </div>
            </section>}

          <section className="composition final">
            <a name={TEXT.compositionBreadcrumbsLabel}></a>
            <Heading>
              <div style={{ float: "left" }}>{TEXT.portfolioCompositionLabel}</div>
              <div className="options">
                <Radio
                  values={[{ value: "Compare view", label: TEXT.compareViewButton }, { value: "Isolate view", label: TEXT.isolateViewButton }]}
                  selected={
                    compositionSplit ? "Isolate view" : "Compare view"
                  }
                  onChange={value =>
                    setCompositionSplit(value === "Isolate view")}
                  disable={bestModel}
                />
              </div>
            </Heading>

            <div className="sub-contents">
              <div className={compositionSplit ? "isolate" : "compare original"}>
                <div className="original version-heading">
                  {originalPortfolioLabel}
                </div>
                <CompositionTable
                  TEXT={TEXT}
                  rows={data.holdings.original}
                  fields={compositionSplit
                    ? compositionFields.filter(field => !field.wtRegion || field.wtRegion === wtRegion)
                    : []}
                  subgroupBy="assetClass"
                  tableType="original"
                  faded={data.uniques.original.map(o => o.ticker)}
                  split={compositionSplit}
                />
              </div>
              <div className={compositionSplit ? "isolate" : "compare proposed"}>
                <div className="proposed version-heading">
                  {proposedPortfolioLabel}
                </div>
                <CompositionTable
                  TEXT={TEXT}
                  rows={compositionSplit
                    ? data.holdings.proposed
                      .map(o => o && { ...o, weighting: o.tickerWeighting })
                      .filter(o => o.weighting)
                    : data.holdings.proposed}
                  fields={compositionSplit
                    ? compositionFields.filter(field => !field.wtRegion || field.wtRegion === wtRegion)
                    : compositionFields.filter(o => o.key === "weighting")}
                  subgroupBy="assetClass"
                  tableType="proposed"
                  split={compositionSplit}
                />
              </div>
            </div>

            {!!performanceTableData.length && <div>
              <div className="contents">
                <h4 className="subtitle">{TEXT.fundStandardPerformanceHeaderLabel}</h4>
                <CompositionTable
                  TEXT={TEXT}
                  rows={performanceTableData}
                  fields={performanceTableFields.filter(o => o.group !== "CP")}
                  footnote={true}
                  alternateFormat={true}
                  faded={data.uniques.original.map(o => o.ticker)}
                  performanceTitle={TEXT.returnsPerformanceTableLabel}
                  performanceTitleAlt={TEXT.tickerFundDetailsTableLabel}
                  wtRegion={wtRegion}
                  width={width} />
                <Compliance dang={TEXT.fundStandardPerformanceDisclosure} />
              </div>

              <div className="contents">
                <h4 className="subtitle">{TEXT.fundDetailsSubHeadingLabel}</h4>
                <CompositionTable
                  TEXT={TEXT}
                  rows={performanceTableData}
                  fields={fundDetailsFields.filter(field => !field.wtRegion || field.wtRegion === wtRegion)}
                  footnote={false}
                  alternateFormat={false}
                  faded={data.uniques.original.map(o => o.ticker)}
                  performanceTitle={TEXT.returnsPerformanceTableLabel}
                  performanceTitleAlt={TEXT.tickerFundDetailsTableLabel}
                  tableType="fundDetails"
                  width={width}
                  dialogLeavingWTSite={dialogLeavingWTSite} />
                <Compliance dang={TEXT.fundDetailsDisclosure} />
              </div>
            </div>}

          </section>

        </Hawsehole>
      </div>
    );
  }

  // Format the fundamentals by key name
  formatFundamentals(key, value) {

    switch (key) {

      case 'Up Capture':
      case 'Down Capture':
      case 'Alpha':
        return format(".2%")(value)

      default:
        return format(".2f")(value)
    }

  }

}

const colorScale = createSelector(() =>
  scaleOrdinal([
    getComputedStyle(document.getElementsByClassName('App')[0]).getPropertyValue('--white-label-color-seven') || "#00A9E4",
    getComputedStyle(document.getElementsByClassName('App')[0]).getPropertyValue('--white-label-color-one') || "#3a6cc9"
  ]).domain(["before", "after"])
);

const mapStateToProps = createStructuredSelector({
  data: dataSelector,
  compareMode: state => state.entry.compareMode,
  wtRegion: state => state.wtRegion,
  currency: state => state.entry.original.entityCCY,
  performanceSplit: state => state.performanceSplit,
  incomeSplit: state => state.incomeSplit,
  compositionSplit: state => state.compositionSplit,
  monteCarloSplit: state => state.monteCarloSplit,
  keyMetrics: state => state.keyMetrics,
  compositionFields: state => state.compositionFields,
  famaFrench5FactorFields: state => state.famaFrench5FactorFields,
  performanceTableFields: state => state.performanceTableFields,
  fundDetailsFields: state => state.fundDetailsFields,
  stressTests: state => state.stressTestKeys,
  colorScale,
  pdfStatus: state => state.pdfStatus,
  ticketStatus: state => state.ticketStatus,
  graphicLayout: state => state.graphic.layout,
  graphicAggregateBy: state => state.graphic.aggregateBy,
  exposureOptions: state => state.exposureOptions,
  investmentGoal: state => state.entry.original.investmentGoal,
  bestModel: state => state.entry.original.bestModel,
  originalPortfolioName: state => state.entry.original.name,
  proposedPortfolioName: state => state.entry.proposed.name,
  clientPdf: state => state.clientPdf,
  salesforceComplete: state => state.salesforceComplete,
  TEXT: state => state.complianceText,
  dialog: state => state.dialog,
  loading: state => state.loading,
  features: state => state.features,
  originalUsingModel: state => state.entry.original.usingModel,
  proposedUsingModel: state => state.entry.proposed.usingModel,
  pendingSaveOriginal: state => state.pendingSaveToMyPortfolios.original,
  pendingSaveProposed: state => state.pendingSaveToMyPortfolios.proposed,
  reportName: state => state.reportName,
});

const mapDispatchToProps = dispatch => ({
  dialogLeavingWTSite: url => {
    if (url.match(/(wisdomtree.com|wisdomtree.ca|wisdomtree.eu)/)) {
      window.open(url, '_blank')
    } else {
      dispatch({ type: 'WT_DIALOG', url })
    }
  },
  // openPortfoliosMatchDialog: message => {
  //   dispatch({ type: 'DIALOG', message, buttons: [] })
  // },
  setPerformanceSplit: split =>
    dispatch({ type: "SET_PERFORMANCE_SPLIT", split }),
  setIncomeSplit: split => dispatch({ type: "SET_INCOME_SPLIT", split }),
  setCompositionSplit: split =>
    dispatch({ type: "SET_COMPOSITION_SPLIT", split }),
  setGraphicLayout: layout =>
    dispatch({ type: "SET_GRAPHIC_LAYOUT", layout }),
  setGraphicAggregateBy: aggregateBy =>
    dispatch({ type: "SET_GRAPHIC_AGGREGATION", aggregateBy }),
  setMonteCarloSplit: split =>
    dispatch({ type: 'SET_MONTE_CARLO_SPLIT', split }),
  retrievePDF: () => dispatch(retrievePDF),
  retrieveTicket: () => dispatch(retrieveTicket),
  clearAnalysis: () => dispatch({ type: 'CLEAR_ANALYSIS' }),

  // If the report name is changed then change it and make sure the pdf status is unsent so 
  // all the elements (e.g., exposures) show up in the pdf
  setReportName: name => {
    dispatch({ type: 'SET_REPORT_NAME', name })
    dispatch({ type: 'SET_PDF_STATUS', status: "unsent" })
  },
  
  // Save the personal Strategy by updating the # location
  saveToPersonalStrategies(portfolioId, side, name) {
    // Update the Hash URL to force dpd to reload
    window.location = '#dpd&saveToMyPortfolios=' + portfolioId + '&portfolioName=' + encodeURI(name);
    dispatch({ type: 'SAVING_TO_MY_PORTFOLIOS', side: side, portfolioId: portfolioId, name: name });
    dispatch(loadPortfolioHistory);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Report);



// WEBPACK FOOTER //
// ./src/Report/index.js